import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { env } from '@/env';
import { guardiaOspite } from '@/services/guardiaOspite';
import { getQsocialsAccessToken } from '@/lib/logto';
import { WATCH_TIMEOUT_MS } from '@/lib/qsocials/codaWatch';
import { prendiIdEvento } from '@/lib/qsocials/idEvento';
import type {
  BlockedFriendshipDTO,
  BlockedListResponse,
  CheerAggregateResponse,
  CheerAllowedResponse,
  CheerEmoji,
  CheerPostResponse,
  FollowCheckResponse,
  FollowDTO,
  FollowersListResponse,
  FollowingListResponse,
  FriendRequestsResponse,
  FriendsListResponse,
  FriendshipDTO,
  GetMeResponse,
  HeartbeatInput,
  MeDTO,
  OnlineFeedResponse,
  PresenceResponse,
  PublicProfileDTO,
  PublicProfileResponse,
  UpdateShowPresenceResponse,
  ResolveUsersByEmailInput,
  ResolveUsersByEmailResponse,
  ResolveUsersByEmailResult,
  ResolveUsersByExternalIdInput,
  ResolveUsersByExternalIdResponse,
  ResolveUsersByExternalIdResult,
  ResolveProfilesBySubsInput,
  ResolveProfilesBySubsResponse,
  ResolveProfilesBySubsResult,
  UpsertMyProfileInput,
  UpsertMyProfileResponse,
  UserProfileDTO,
  UserSearchResponse,
  UserSearchResult,
  WatchResponse,
} from '@/types/social';

/**
 * QSocials API client — friend/follow, presence, co-watching, cheers.
 *
 * Auth: dedicated Logto API resource `https://qsocials.qlash.gg`. Token is
 * minted on demand via the shared refresh_token (see
 * `getQsocialsAccessToken()` in src/lib/logto.ts). Same pattern as
 * qriots/qbot.
 *
 * Dev: env.qsocialsApiUrl = '/qsocials-api', Vite proxy forwards to the
 * local qsocials-api on port 3010.
 * Prod: env.qsocialsApiUrl = 'https://qsocials.qlash.gg'.
 *
 * Note: qsocials emits real-time updates via Centrifugo channels
 * (`tournament:<id>:watchers`, `match:<id>:cheers`, `presence:user:<id>`)
 * — but in dev the Centrifugo publisher is stub log-only. The hooks layer
 * (`useCentrifugoChannel`) is wired but useless until Centrifugo is wired
 * up; until then RTK Query polling is the source of truth.
 */
export const socialApi = createApi({
  reducerPath: 'socialApi',
  tagTypes: [
    'Friends',
    'FriendRequests',
    // GET /social/friends/blocked — i blocchi messi DA ME (punto 7 del ridisegno Amici). Lo
    // invalidano il blocco e lo sblocco: la pagina «Account bloccati» e il numero sulla sua
    // riga non devono restare indietro di un'azione.
    'Blocked',
    'Following',
    'Followers',
    'FollowCheck',
    'UserProfile',
    'PresenceMe',
    'OnlineFriends',
    'Watchers',
    'Cheers',
    'CheerAllowed',
    // GET/PATCH /social/me — solo `showPresence` (L-2418, PR-2 del ridisegno Amici). Non e'
    // 'UserProfile': quel tag e' per il profilo PUBBLICO di un altro utente.
    'Me',
    // DM: li invalida il tempo reale (`lib/qsocials/tempoReale.ts`) su `dm.message` e a ogni
    // sottoscrizione; li forniranno gli endpoint `/social/dm` quando arrivano.
    'DmConversations',
    'DmUnread',
    // I messaggi di UNA conversazione: `{ type: 'DmMessages', id: conversationId }` (`services/dmApi.ts`).
    'DmMessages',
  ],
  /**
   * 🔴 Il secondo cancello: a QSocials spento **nessuna richiesta esce dal browser**.
   *
   * Non basta togliere il token (`getQsocialsAccessToken` torna gia' `null`): senza
   * questo strato le chiamate partirebbero lo stesso, **senza `Authorization`, verso
   * `env.qsocialsApiUrl` — che di default e' l'host di PRODUZIONE**. Una richiesta
   * anonima a un servizio vero e' meno grave di una autenticata, e resta una richiesta
   * che una build di collaudo non deve fare.
   *
   * L'errore restituito e' esplicito e non si confonde con la rete: chi lo legge deve
   * capire che e' una scelta di build, non un guasto.
   */
  baseQuery: guardiaOspite('qsocials', async (args, api, extra) => {
    if (!env.featureSocialEnabled) {
      return {
        error: {
          status: 'CUSTOM_ERROR' as const,
          error: 'qsocials disabilitato in questa build (VITE_FEATURE_SOCIAL_ENABLED=false)',
        },
      };
    }
    return fetchBaseQuery({
      baseUrl: env.qsocialsApiUrl,
      prepareHeaders: async (headers, { endpoint }) => {
        const token = await getQsocialsAccessToken();
        // La rilettura causata da un evento del tempo reale porta il suo id (prova di R3):
        // `lib/qsocials/idEvento.ts`. Nessun'altra richiesta lo porta.
        const idEvento = prendiIdEvento(endpoint);
        if (idEvento) headers.set('X-Request-Id', idEvento);
        if (token) headers.set('Authorization', `Bearer ${token}`);
        headers.set('Accept', 'application/json');
        headers.set('Content-Type', 'application/json');
        return headers;
      },
    })(args, api, extra);
  }),
  endpoints: (builder) => ({
    // ---------------- User profiles (Wave 2) ----------------

    /**
     * Upsert the authenticated user's public profile (displayName + avatar).
     * Called once per session by useProfileSync from data derived off the
     * Telegram user. Idempotent server-side. Does not invalidate any list
     * tags: this writes *my* profile (how others see me), not my own lists.
     */
    upsertMyProfile: builder.mutation<UserProfileDTO, UpsertMyProfileInput>({
      query: (body) => ({
        url: '/social/me/profile',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: UpsertMyProfileResponse) => response.profile,
    }),

    /**
     * `GET /social/me` — punto 6 del ridisegno Amici (L-2418, PR-2). Serve
     * SOLO `showPresence` per il gruppo Privacy: il resto del payload
     * (displayName/avatarUrl/externalUserId) non ha oggi un consumatore qui.
     */
    getMe: builder.query<MeDTO, void>({
      query: () => ({ url: '/social/me' }),
      transformResponse: (response: GetMeResponse) => response.me,
      providesTags: ['Me'],
    }),

    /**
     * `PATCH /social/me` — l'interruttore «Mostra quando sono online». Il
     * server pubblica il diff di presenza (offline immediato allo spegnimento,
     * lo stato vero alla riaccensione): il client non tocca l'heartbeat.
     */
    updateShowPresence: builder.mutation<boolean, { showPresence: boolean }>({
      query: (body) => ({
        url: '/social/me',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: UpdateShowPresenceResponse) => response.showPresence,
      // Revisione indipendente #492, punto 3: il valore mostrato deve venire dalla RISPOSTA
      // della PATCH, non solo dalla rilettura che `invalidatesTags` innesca. Senza questo,
      // «PATCH 200 e poi GET di rilettura fallita» lascia lo switch sul valore VECCHIO — un
      // fallimento di rete travolgerebbe un salvataggio riuscito. Nessun ottimismo prima
      // della risposta (niente da disfare: il file non promette nulla finché il server non
      // ha parlato), solo la scrittura della cache a conferma avvenuta.
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: confirmed } = await queryFulfilled;
          dispatch(
            socialApi.util.updateQueryData('getMe', undefined, (draft) => {
              if (draft) draft.showPresence = confirmed;
            }),
          );
        } catch {
          // La PATCH e' fallita: niente da scrivere in cache, `isError` lo dice alla UI.
        }
      },
      invalidatesTags: ['Me'],
    }),

    /**
     * `PATCH /social/me` — l'interruttore «Fai sapere quando gioco» (contratto di fronte r38 §1-bis).
     * Il corpo e' SOLO `{ shareLiveMatches }`: non si manda `showPresence` insieme, sono due interruttori.
     * La forma della risposta non e' nel contratto: se porta `shareLiveMatches` booleano la si scrive in
     * cache, altrimenti vale la rilettura (`invalidatesTags`). Mai il valore INVIATO preso per confermato.
     */
    updateShareLiveMatches: builder.mutation<boolean | null, { shareLiveMatches: boolean }>({
      query: (body) => ({
        url: '/social/me',
        method: 'PATCH',
        body,
      }),
      transformResponse: (response: { shareLiveMatches?: unknown } | null | undefined) =>
        typeof response?.shareLiveMatches === 'boolean' ? response.shareLiveMatches : null,
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data: confirmed } = await queryFulfilled;
          if (confirmed === null) return;
          dispatch(
            socialApi.util.updateQueryData('getMe', undefined, (draft) => {
              if (draft) draft.shareLiveMatches = confirmed;
            }),
          );
        } catch {
          // La PATCH e' fallita: niente da scrivere in cache, `isError` lo dice alla UI.
        }
      },
      invalidatesTags: ['Me'],
    }),

    /**
     * Server-side discovery search by displayName. Requires q ≥ 2 chars
     * (callers must `skip` shorter queries). Excludes the requester
     * server-side. `limit` defaults to 20 on the backend.
     */
    searchUsers: builder.query<UserSearchResult[], { q: string; limit?: number }>({
      query: ({ q, limit }) => ({
        url: '/social/users/search',
        params: { q, ...(limit != null ? { limit } : {}) },
      }),
      transformResponse: (response: UserSearchResponse) => response.results,
    }),

    /**
     * A user's public social profile by Logto `sub`: identity + the
     * relationship to the caller (friendStatus / iFollow / followsMe /
     * canMessage / mutualFriends) + counts + gated presence. Backs the
     * UserProfile screen (`/profile/:id`). Relationship-changing mutations
     * (friend/follow/block) invalidate `{ UserProfile, id }` so the screen
     * reflects the new state. 404 → user_not_found (truly unknown sub).
     */
    getUserProfile: builder.query<PublicProfileDTO, string>({
      query: (userId) => ({ url: `/social/users/${encodeURIComponent(userId)}` }),
      transformResponse: (response: PublicProfileResponse) => response.profile,
      providesTags: (_r, _e, userId) => [{ type: 'UserProfile', id: userId }],
    }),

    /**
     * Identity bridge: resolve a batch of login emails → Logto `sub`.
     * Server-side resolver (Logto Management API, cached). Emails with no
     * Logto match are omitted from `results`. The caller builds an
     * email → sub map to light up <PlayerSocialButtons> for participants
     * that resolve. A query (not a mutation) so RTK Query caches the batch
     * per email-set and dedupes re-renders of the participants list.
     */
    resolveUsersByEmail: builder.query<
      ResolveUsersByEmailResult[],
      ResolveUsersByEmailInput
    >({
      query: (body) => ({
        url: '/social/users/resolve-by-email',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ResolveUsersByEmailResponse) =>
        response.results,
    }),

    /**
     * Deterministic identity bridge: resolve a batch of Laravel `users.id`
     * (digits-only) → Logto `sub`. Each user self-publishes their own
     * `externalUserId` on launch (useProfileSync), so this lights up
     * <PlayerSocialButtons> even for participants whose login email doesn't
     * match Logto (Apple "Hide My Email", social login). Ids with no published
     * profile are omitted from `results`. Preferred over resolve-by-email;
     * email is only the fallback. Cap ≤ 100 ids per call. A query (not a
     * mutation) so RTK Query caches the batch per id-set.
     */
    resolveUsersByExternalId: builder.query<
      ResolveUsersByExternalIdResult[],
      ResolveUsersByExternalIdInput
    >({
      query: (body) => ({
        url: '/social/users/resolve-by-external-id',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ResolveUsersByExternalIdResponse) =>
        response.results,
    }),

    /**
     * Reverse identity bridge: resolve a batch of Logto `subs` → public profile (displayName + avatar) AND the
     * caller's `relation` / `following` for each. Puts human names on lists that only know subs (QBank league
     * standings) and draws the social gestures on the backgammon match history. The server reads QSocials
     * `userProfiles` ONLY (no Logto fallback): since QSocials #29 there is ONE entry per sub, and a sub with no profile
     * comes back with `displayName: null` and its TRUE relation, and whoever blocks the caller as a sub never seen
     * (`none`) — a server older than #29 omits them instead.
     * Cap ≤ 100 (a 400 above). A query so RTK Query caches per sub-set. Served by QSocials #28 (staging,
     * 2026-09-30); a server without it answers 404, and the callers degrade to «no names / no gestures».
     *
     * ⚠️ No tag: a gesture does not refetch it. The history panel keeps the state a gesture changes in ITS OWN store
     * (`storico/StatoAvversari`), one per `sub`, so that every row of the same opponent moves together.
     */
    resolveProfilesBySubs: builder.query<
      ResolveProfilesBySubsResult[],
      ResolveProfilesBySubsInput
    >({
      query: (body) => ({
        url: '/social/users/profiles',
        method: 'POST',
        body,
      }),
      transformResponse: (response: ResolveProfilesBySubsResponse) => response.results,
    }),

    // ---------------- Friendships ----------------

    listFriends: builder.query<FriendshipDTO[], void>({
      query: () => ({ url: '/social/friends' }),
      transformResponse: (response: FriendsListResponse) => response.friends,
      providesTags: ['Friends'],
    }),

    // I blocchi messi DA ME, dal più recente (punto 7 del ridisegno Amici). Nessuna paginazione:
    // la rotta restituisce tutto, e `count` è la lunghezza dell'elenco. I blocchi SUBITI non ci
    // sono mai (K4): la pagina non deve poterli mostrare.
    listBlocked: builder.query<BlockedFriendshipDTO[], void>({
      query: () => ({ url: '/social/friends/blocked' }),
      transformResponse: (response: BlockedListResponse) => response.blocked,
      providesTags: ['Blocked'],
    }),

    listFriendRequests: builder.query<
      { incoming: FriendshipDTO[]; outgoing: FriendshipDTO[] },
      void
    >({
      query: () => ({ url: '/social/friends/requests' }),
      transformResponse: (response: FriendRequestsResponse) => ({
        incoming: response.incoming,
        outgoing: response.outgoing,
      }),
      providesTags: ['FriendRequests'],
    }),

    requestFriendship: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/request',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
      ],
    }),

    acceptFriendship: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/accept',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
        // Tornare amici RIAPRE la conversazione chiusa (§1-ter: «se tornate amici, la ritrovi qui»).
        'DmConversations',
        'DmUnread',
      ],
    }),

    removeFriendship: builder.mutation<void, { friendUserId: string }>({
      query: ({ friendUserId }) => ({
        url: `/social/friends/${encodeURIComponent(friendUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        { type: 'UserProfile', id: friendUserId },
        // Come il blocco: l'amicizia tolta CHIUDE la conversazione (§1-ter `closed`).
        'DmConversations',
        'DmUnread',
      ],
    }),

    blockUser: builder.mutation<FriendshipDTO, { friendUserId: string }>({
      query: (body) => ({
        url: '/social/friends/block',
        method: 'POST',
        body,
      }),
      transformResponse: (response: { friendship: FriendshipDTO }) =>
        response.friendship,
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        'Blocked',
        'Following',
        'Followers',
        { type: 'UserProfile', id: friendUserId },
        { type: 'FollowCheck', id: friendUserId },
        // Il blocco CHIUDE la conversazione (§1-ter `closed`): senza, la riga dell'elenco dei messaggi
        // resterebbe aperta fino al prossimo ricontrollo, e i suoi non letti nel numero di «Messaggi».
        'DmConversations',
        'DmUnread',
      ],
    }),

    unblockUser: builder.mutation<void, { friendUserId: string }>({
      query: ({ friendUserId }) => ({
        url: `/social/friends/block/${encodeURIComponent(friendUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { friendUserId }) => [
        'Friends',
        'FriendRequests',
        'Blocked',
        { type: 'UserProfile', id: friendUserId },
        // Lo stato dell'amicizia cambia: l'elenco dei messaggi si rilegge da `/social/dm/conversations`,
        // che dice lui se la conversazione e' `closed` (il client non lo deduce).
        'DmConversations',
        'DmUnread',
      ],
    }),

    // ---------------- Follows ----------------

    // `total` è il conteggio VERO lato server (non limitato dalla pagina);
    // `items` è la pagina caricata (oggi fissa a 100, v. socialApi ADR). Il
    // tab Amici del 2026-09-28 mostra `total`, mai `items.length` — sopra 100
    // follower/seguiti il secondo si ferma, il primo no.
    listFollowing: builder.query<{ items: FollowDTO[]; total: number }, void>({
      query: () => ({ url: '/social/follows/following' }),
      transformResponse: (response: FollowingListResponse) => ({
        items: response.following,
        total: response.total,
      }),
      providesTags: ['Following'],
    }),

    listFollowers: builder.query<{ items: FollowDTO[]; total: number }, void>({
      query: () => ({ url: '/social/follows/followers' }),
      transformResponse: (response: FollowersListResponse) => ({
        items: response.followers,
        total: response.total,
      }),
      providesTags: ['Followers'],
    }),

    checkFollow: builder.query<
      { iFollow: boolean; followsMe: boolean },
      string
    >({
      query: (userId) => ({
        url: `/social/follows/check/${encodeURIComponent(userId)}`,
      }),
      transformResponse: (response: FollowCheckResponse) => ({
        iFollow: response.iFollow,
        followsMe: response.followsMe,
      }),
      providesTags: (_r, _e, userId) => [{ type: 'FollowCheck', id: userId }],
    }),

    followUser: builder.mutation<void, { followedUserId: string }>({
      query: (body) => ({
        url: '/social/follows',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { followedUserId }) => [
        'Following',
        { type: 'FollowCheck', id: followedUserId },
        { type: 'UserProfile', id: followedUserId },
      ],
    }),

    unfollowUser: builder.mutation<void, { followedUserId: string }>({
      query: ({ followedUserId }) => ({
        url: `/social/follows/${encodeURIComponent(followedUserId)}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { followedUserId }) => [
        'Following',
        { type: 'FollowCheck', id: followedUserId },
        { type: 'UserProfile', id: followedUserId },
      ],
    }),

    // ---------------- Presence ----------------

    heartbeat: builder.mutation<PresenceResponse, HeartbeatInput>({
      query: (body) => ({
        url: '/social/presence/heartbeat',
        method: 'POST',
        body,
      }),
      // Heartbeat does not invalidate anything by default — it's a high-frequency
      // mutation. Online-friends feed is polled and will reflect changes
      // naturally; invalidating would cause needless refetches.
    }),

    goOffline: builder.mutation<void, void>({
      query: () => ({
        url: '/social/presence/offline',
        method: 'POST',
      }),
      invalidatesTags: ['PresenceMe', 'OnlineFriends'],
    }),

    getMyPresence: builder.query<PresenceResponse, void>({
      query: () => ({ url: '/social/presence/me' }),
      providesTags: ['PresenceMe'],
    }),

    getOnlineFriendsFeed: builder.query<OnlineFeedResponse, void>({
      query: () => ({ url: '/social/presence/feed/online-friends' }),
      providesTags: ['OnlineFriends'],
    }),

    getUserPresence: builder.query<PresenceResponse, string>({
      query: (userId) => ({
        url: `/social/presence/${encodeURIComponent(userId)}`,
      }),
    }),

    // ---------------- Watch (co-watching) ----------------

    joinWatch: builder.mutation<WatchResponse, { tournamentId: string }>({
      query: ({ tournamentId }) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
        method: 'POST',
        // Un tempo massimo: la coda del co-watch (`codaWatch.ts`) aspetta la RISPOSTA di ogni join/leave, e una
        // richiesta appesa fermerebbe quel torneo per tutta la sessione. Un timeout e' un esito e la coda va avanti.
        timeout: WATCH_TIMEOUT_MS,
      }),
      invalidatesTags: (_r, _e, { tournamentId }) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    leaveWatch: builder.mutation<WatchResponse, { tournamentId: string }>({
      query: ({ tournamentId }) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
        method: 'DELETE',
        timeout: WATCH_TIMEOUT_MS, // v. joinWatch
      }),
      invalidatesTags: (_r, _e, { tournamentId }) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    getWatchers: builder.query<WatchResponse, string>({
      query: (tournamentId) => ({
        url: `/social/watch/${encodeURIComponent(tournamentId)}`,
      }),
      providesTags: (_r, _e, tournamentId) => [
        { type: 'Watchers', id: tournamentId },
      ],
    }),

    // ---------------- Cheers ----------------

    getAllowedCheers: builder.query<readonly string[], void>({
      query: () => ({ url: '/social/cheers/allowed' }),
      transformResponse: (response: CheerAllowedResponse) => response.allowed,
      providesTags: ['CheerAllowed'],
      // Allowed list rarely changes. Keep cached for the whole session.
      keepUnusedDataFor: 86400,
    }),

    postCheer: builder.mutation<
      CheerPostResponse,
      { matchId: string; emoji: CheerEmoji | string }
    >({
      query: (body) => ({
        url: '/social/cheers',
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { matchId }) => [
        { type: 'Cheers', id: matchId },
      ],
    }),

    getCheers: builder.query<CheerAggregateResponse, string>({
      query: (matchId) => ({
        url: `/social/cheers/${encodeURIComponent(matchId)}`,
      }),
      providesTags: (_r, _e, matchId) => [{ type: 'Cheers', id: matchId }],
    }),
  }),
});

export const {
  useUpsertMyProfileMutation,
  useGetMeQuery,
  useUpdateShowPresenceMutation,
  useUpdateShareLiveMatchesMutation,
  useSearchUsersQuery,
  useGetUserProfileQuery,
  useResolveUsersByEmailQuery,
  useResolveUsersByExternalIdQuery,
  useResolveProfilesBySubsQuery,
  useLazyResolveProfilesBySubsQuery,
  useListFriendsQuery,
  useListFriendRequestsQuery,
  useRequestFriendshipMutation,
  useAcceptFriendshipMutation,
  useRemoveFriendshipMutation,
  useBlockUserMutation,
  useUnblockUserMutation,
  useListBlockedQuery,
  useListFollowingQuery,
  useListFollowersQuery,
  useCheckFollowQuery,
  useFollowUserMutation,
  useUnfollowUserMutation,
  useHeartbeatMutation,
  useGoOfflineMutation,
  useGetMyPresenceQuery,
  useGetOnlineFriendsFeedQuery,
  useGetUserPresenceQuery,
  useJoinWatchMutation,
  useLeaveWatchMutation,
  useGetWatchersQuery,
  useGetAllowedCheersQuery,
  usePostCheerMutation,
  useGetCheersQuery,
} = socialApi;
